import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, InputAdornment, TextField } from '@mui/material';
import { CalendarToday } from '@mui/icons-material';

interface DateMaskFieldProps {
  label: string;
  value?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  name?: string;
  comHora?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  fullWidth?: boolean;
  margin?: 'none' | 'dense' | 'normal';
  disabled?: boolean;
  required?: boolean;
  size?: 'small' | 'medium';
  autoFocus?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
}

function somenteDigitos(valor: string): string {
  return valor.replace(/\D/g, '');
}

function aplicarMascara(digitos: string, comHora: boolean): string {
  const max = comHora ? 12 : 8;
  const d = digitos.slice(0, max);
  let out = '';
  for (let i = 0; i < d.length; i++) {
    if (i === 2 || i === 4) out += '/';
    if (comHora && i === 8) out += ' ';
    if (comHora && i === 10) out += ':';
    out += d[i];
  }
  return out;
}

function paraExibicao(valor: string | undefined, comHora: boolean): string {
  if (!valor) return '';
  const [data, tempo] = valor.split('T');
  const [ano, mes, dia] = data.split('-');
  if (!ano || !mes || !dia || ano.length !== 4) return '';
  let out = `${dia}/${mes}/${ano}`;
  if (comHora && tempo) {
    const [hora, minuto] = tempo.split(':');
    if (hora && minuto) out += ` ${hora}:${minuto}`;
  }
  return out;
}

function paraIso(digitos: string, comHora: boolean): string {
  const data = `${digitos.slice(4, 8)}-${digitos.slice(2, 4)}-${digitos.slice(0, 2)}`;
  if (!comHora) return data;
  return `${data}T${digitos.slice(8, 10)}:${digitos.slice(10, 12)}`;
}

const DateMaskField: React.FC<DateMaskFieldProps> = ({
  label,
  value,
  onChange,
  onBlur,
  name,
  comHora = false,
  error,
  helperText,
  fullWidth = true,
  margin = 'normal',
  disabled,
  required,
  size,
  autoFocus,
  inputRef,
}) => {
  const nativeRef = useRef<HTMLInputElement>(null);
  const [display, setDisplay] = useState(() => paraExibicao(value, comHora));

  useEffect(() => {
    setDisplay(paraExibicao(value, comHora));
  }, [value, comHora]);

  const max = comHora ? 12 : 8;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digitos = somenteDigitos(e.target.value).slice(0, max);
    setDisplay(aplicarMascara(digitos, comHora));
    onChange(digitos.length === max ? paraIso(digitos, comHora) : '');
  };

  const handleNativeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (!v) return;
    setDisplay(paraExibicao(v, comHora));
    onChange(v);
  };

  const abrirPicker = () => {
    const el = nativeRef.current;
    if (!el) return;
    const comShowPicker = el as HTMLInputElement & { showPicker?: () => void };
    if (typeof comShowPicker.showPicker === 'function') comShowPicker.showPicker();
    else el.click();
  };

  const valorNativo = (() => {
    if (!value) return '';
    if (comHora) return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value) ? value.slice(0, 16) : '';
    return /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : '';
  })();

  return (
    <Box sx={{ position: 'relative' }}>
      <TextField
        fullWidth={fullWidth}
        label={label}
        name={name}
        value={display}
        onChange={handleChange}
        onBlur={onBlur}
        inputRef={inputRef}
        error={error}
        helperText={helperText}
        margin={margin}
        disabled={disabled}
        required={required}
        size={size}
        autoFocus={autoFocus}
        placeholder={comHora ? 'dd/mm/aaaa hh:mm' : 'dd/mm/aaaa'}
        InputLabelProps={{ shrink: true }}
        inputProps={{ inputMode: 'numeric' }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={abrirPicker} edge="end" size="small" tabIndex={-1} disabled={disabled}>
                <CalendarToday fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <input
        ref={nativeRef}
        type={comHora ? 'datetime-local' : 'date'}
        value={valorNativo}
        onChange={handleNativeChange}
        tabIndex={-1}
        aria-hidden="true"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          border: 0,
          opacity: 0,
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
};

export default DateMaskField;
